import { useState, useEffect, useRef } from 'react'
import { Plus, Clock, ChevronDown } from 'lucide-react'
import { formatNaira } from '../../utils/formatters'

const PRODUCTS = ['AGO', 'PMS', 'DPK']
const PRODUCT_COLORS = {
  AGO: 'bg-amber-100 text-amber-700 border-amber-200',
  PMS: 'bg-blue-100 text-blue-700 border-blue-200',
  DPK: 'bg-green-100 text-green-700 border-green-200',
}

// datetime-local wants local wall-clock, not an ISO/UTC string.
function toLocalInput(d) {
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

function PriceUpdateModal({ product, currentPrice, branchId, onUpdate, onClose }) {
  const [value, setValue] = useState(currentPrice ? String(currentPrice.unitPrice) : '')
  const [effectiveFrom, setEffectiveFrom] = useState(() => toLocalInput(new Date()))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const when = new Date(effectiveFrom)
  const isPast = when.getTime() < Date.now() - 60_000
  const isFuture = when.getTime() > Date.now() + 60_000

  async function handleSubmit(e) {
    e.preventDefault()
    const n = parseFloat(value)
    if (!n || n <= 0) { setError('Enter a valid price.'); return }
    if (Number.isNaN(when.getTime())) { setError('Enter a valid date and time.'); return }
    setSaving(true)
    const result = await onUpdate(branchId, product, n, effectiveFrom)
    setSaving(false)
    if (!result.success) { setError(result.message); return }
    onClose()
  }

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/50" onClick={onClose} />
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[70] bg-white rounded-2xl shadow-2xl p-5 max-w-sm mx-auto max-h-[calc(100dvh-6rem)] overflow-y-auto">
        <h3 className="font-bold text-gray-800 mb-1">Update {product} Price</h3>
        <p className="text-xs text-gray-400 mb-4">Creates a new price entry — history is preserved.</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Price (₦ per litre)</label>
            <input
              type="number"
              value={value}
              onChange={(e) => { setValue(e.target.value); setError('') }}
              placeholder="e.g. 1200"
              step="0.01"
              min="0.01"
              inputMode="decimal"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-xl font-bold"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Takes effect from</label>
            <input
              type="datetime-local"
              value={effectiveFrom}
              onChange={(e) => { setEffectiveFrom(e.target.value); setError('') }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm"
            />
            {isPast && (
              <p className="mt-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Backdating records when the price actually changed. It does <b>not</b> re-price
                sales already recorded — each one keeps the price it was written at.
              </p>
            )}
            {isFuture && (
              <p className="mt-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                Scheduled. Sales keep using the current price until this time.
              </p>
            )}
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
          <div className="flex space-x-3">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm disabled:opacity-60">
              {saving ? 'Saving…' : 'Set Price'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

export default function PriceManagementPanel({ currentUser, branches, prices, priceHistory, onUpdatePrice }) {
  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'superadmin'
  const [editing, setEditing] = useState(null) // { product, branchId, currentPrice }
  const [expandedBranch, setExpandedBranch] = useState(null)

  const visibleBranches = currentUser.role === 'manager'
    ? branches.filter((b) => b.id === currentUser.branchId)
    : branches

  // This panel is the credit section's landing screen, so open the first
  // station once its data arrives — otherwise it lands on nothing but a list
  // of station names. Only until the user touches it; after that they decide.
  const autoExpanded = useRef(false)
  useEffect(() => {
    if (!autoExpanded.current && visibleBranches.length > 0) {
      autoExpanded.current = true
      setExpandedBranch(visibleBranches[0].id)
    }
  }, [visibleBranches])

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-bold text-gray-800">Price Management</h2>
        <p className="text-xs text-gray-400">Current prices per product. Updates create a new history record.</p>
      </div>

      {visibleBranches.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-8">No branches found.</p>
      )}

      {visibleBranches.map((branch) => (
        <div key={branch.id} className="bg-white rounded-xl border border-gray-100 shadow overflow-hidden">
          <button
            onClick={() => setExpandedBranch(expandedBranch === branch.id ? null : branch.id)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition"
          >
            <p className="font-semibold text-sm text-gray-800">{branch.name}</p>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-400">{branch.location}</span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expandedBranch === branch.id ? 'rotate-180' : ''}`} />
            </div>
          </button>

          {expandedBranch === branch.id && (
            <>
              <div className="px-4 pb-4 grid grid-cols-3 gap-2 border-t border-gray-50">
                {PRODUCTS.map((p) => {
                  const price = prices[branch.id]?.[p]
                  // buildPriceMap hides future rows, so find them here.
                  const scheduled = (priceHistory[branch.id]?.[p] || [])
                    .filter((h) => new Date(h.effectiveFrom).getTime() > Date.now())
                    .sort((a, b) => new Date(a.effectiveFrom) - new Date(b.effectiveFrom))[0]
                  return (
                    <div key={p} className={`rounded-xl border p-3 ${PRODUCT_COLORS[p]}`}>
                      <p className="text-xs font-bold mb-1">{p}</p>
                      {price ? (
                        <p className="text-sm font-bold">{formatNaira(price.unitPrice)}<span className="text-xs font-normal">/L</span></p>
                      ) : (
                        <p className="text-xs opacity-60">Not set</p>
                      )}
                      {scheduled && (
                        <p className="mt-1 text-[10.5px] font-semibold opacity-80">
                          {formatNaira(scheduled.unitPrice)} from {new Date(scheduled.effectiveFrom).toLocaleString('en-NG', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => setEditing({ product: p, branchId: branch.id, currentPrice: price })}
                          className="mt-2 flex items-center space-x-1 text-xs font-semibold opacity-70 hover:opacity-100 transition"
                        >
                          <Plus className="w-3 h-3" />
                          <span>Update</span>
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Price history per branch */}
              <div className="px-4 pb-4 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-3 mb-2 flex items-center space-x-1">
                  <Clock className="w-3 h-3" /><span>Price History</span>
                </p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {PRODUCTS.flatMap((p) =>
                    (priceHistory[branch.id]?.[p] || []).map((h) => (
                      <div key={h.id} className="flex justify-between text-xs py-1 border-b border-gray-50">
                        <span className="font-mono text-gray-500">{new Date(h.effectiveFrom).toLocaleDateString('en-NG')}</span>
                        <span className="font-semibold text-gray-600">{p}</span>
                        <span className="font-bold text-gray-800">{formatNaira(h.unitPrice)}/L</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      ))}

      {editing && (
        <PriceUpdateModal
          product={editing.product}
          branchId={editing.branchId}
          currentPrice={editing.currentPrice}
          onUpdate={onUpdatePrice}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}
